import {
  Badge,
  Button,
  Calendar,
  Checkbox,
  cn,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  SheetTitle,
  Input,
  Label,
  Popover,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetFooter,
  Separator,
  PopoverTrigger,
  PopoverContent,
  useIsMobile,
} from '@hub-la/shadcn'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useFormik } from 'formik'
import { Check, ChevronDown } from 'lucide-react'
import moment from 'moment'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { Offer } from '../../domain/dtos/offer'
import { LifetimeInterval, RecurringInterval } from '../../domain/enums/interval'
import { useCreateCoupon } from '../hooks/use-create-coupon'

export type Values = {
  code: string
  discountPercent?: number
  applyToSpecificOffers: boolean
  offers: Offer[]
  applyToSpecificIntervals: boolean
  intervals: (RecurringInterval | LifetimeInterval)[]
  maxAllowedTotalUsages?: number
  maxAllowedTotalUsagesPerUser?: number
  validUntil?: string
  isRecurrent: boolean
  useOutsideCheckout: boolean
}

type CreateCouponModalProps = {
  isOpen: boolean
  isLoading: boolean
  onClose: () => void
  onSuccess: () => void
  offers: Offer[]
}

const validationSchema = Yup.object().shape({
  code: Yup.string()
    .min(4, 'O código deve ter no mínimo 4 caracteres.')
    .max(15, 'O código deve ter no máximo 15 caracteres.')
    .matches(/^[A-Za-z0-9]*$/, 'O código deve conter apenas letras e números.')
    .required('Código do cupom é obrigatório'),
  discountPercent: Yup.number()
    .required('Porcentagem de desconto é obrigatória')
    .min(1, 'O desconto mínimo é 1%')
    .max(90, 'O desconto máximo é 90%'),
  maxAllowedTotalUsages: Yup.number().when('isLimitUsageEnabled', {
    is: true,
    then: Yup.number().required('Número máximo de usos é obrigatório quando o limite está ativado'),
  }),
  validUntil: Yup.date().when('isValidUntilEnabled', {
    is: true,
    then: Yup.date()
      .min(new Date(), 'A data de validade deve ser no futuro')
      .required('Data de validade é obrigatória quando ativada'),
  }),
})

export const CreateCouponModal: React.FC<CreateCouponModalProps> = ({
  isOpen,
  onClose,
  offers,
  isLoading,
  onSuccess,
}) => {
  const [isLimitUsageEnabled, setIsLimitUsageEnabled] = useState<boolean>(false)
  const [isValidUntilEnabled, setIsValidUntilEnabled] = useState<boolean>(false)

  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const { mutateAsync: createCoupon, isLoading: loading, error } = useCreateCoupon()

  const { values, isValid, submitForm, setFieldValue, resetForm, handleBlur, errors, touched, handleChange, dirty } =
    useFormik<Values>({
      initialValues: {
        code: '',
        validUntil: undefined,
        applyToSpecificOffers: false,
        offers: [],
        applyToSpecificIntervals: false,
        intervals: [],
        discountPercent: undefined,
        maxAllowedTotalUsages: undefined,
        maxAllowedTotalUsagesPerUser: undefined,
        isRecurrent: false,
        useOutsideCheckout: false,
      },
      validationSchema,
      validateOnChange: true,
      validateOnBlur: true,
      onSubmit: async (values) => {
        try {
          await createCoupon(values)
          resetForm()
          onSuccess?.()
        } catch (error) {
          console.error('Error creating coupon:', error)
        }
      },
    })

  const maxLength = 15
  const hasCouponError = !!(errors.code && touched.code)

  const getHelperText = () => {
    if (hasCouponError) return errors.code
    if (error) return t('createCoupon.alreadyExists')
    return t('createCoupon.codeHelperText', { current: values.code.length, maxLength })
  }

  const onCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    const isValid = /^[A-Za-z0-9]*$/.test(value)
    if (isValid) {
      setFieldValue('code', value.toUpperCase())
    }
  }

  const onCloseModal = () => {
    resetForm()
    onClose()
    setIsLimitUsageEnabled(false)
    setIsValidUntilEnabled(false)
  }

  const DateFormatter: React.FC<{ date?: string; fallbackText?: string }> = ({
    date,
    fallbackText = 'Selecionar data',
  }) => {
    if (!date) return <span className="text-sm font-medium">{fallbackText}</span>
    return <span className="text-sm font-medium">{format(new Date(date), 'dd MMM yyyy', { locale: ptBR })}</span>
  }

  return (
    <Sheet open={isOpen} onOpenChange={onCloseModal}>
      <SheetContent className="p-6 w-full max-w-lg">
        <SheetHeader>
          <SheetTitle className="text-lg text-primary font-semibold">{t('createCoupon.title')}</SheetTitle>
        </SheetHeader>

        <div className="mt-6 flex flex-col gap-6">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="code">{t('createCoupon.code')}</Label>
            <Input
              id="code"
              name="code"
              value={values.code}
              onChange={onCodeChange}
              onBlur={handleBlur}
              maxLength={maxLength}
              aria-invalid={hasCouponError}
              aria-describedby="code-error"
              className={cn(hasCouponError && 'border-red-500')}
            />
            <p id="code-error" className={cn('text-sm', hasCouponError ? 'text-red-500' : 'text-muted-foreground')}>
              {getHelperText()}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="discountPercent">{t('createCoupon.discountPercent')}</Label>
            <Input
              id="discountPercent"
              name="discountPercent"
              type="number"
              value={values.discountPercent}
              onChange={handleChange}
              onBlur={handleBlur}
              min={1}
              max={90}
              aria-invalid={!!(errors.discountPercent && touched.discountPercent && !dirty)}
              aria-describedby="discountPercent-error"
            />
            {errors.discountPercent && touched.discountPercent && !dirty && (
              <p id="discountPercent-error" className="text-sm text-red-500">
                {errors.discountPercent}
              </p>
            )}
            <p className="text-sm text-muted-foreground">{t('createCoupon.discountPercentHelperText')}</p>
          </div>

          <div className="flex flex-col gap-4">
            <p className="font-bold">Opções</p>

            <div className="flex flex-col rounded-lg space-y-4">
              <div className="flex flex-start items-center space-x-2">
                <Checkbox
                  id="applyToSpecificOffers"
                  checked={values.applyToSpecificOffers}
                  onCheckedChange={(checked) => setFieldValue('applyToSpecificOffers', checked)}
                />
                <Label htmlFor="applyToSpecificOffers">{t('createCoupon.applyToSpecificOffers')}</Label>
              </div>

              {values.applyToSpecificOffers && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between">
                      <div className="flex items-center">
                        <span className="text-sm">Ofertas</span>
                        {values.offers.length > 0 && (
                          <>
                            <Separator orientation="vertical" className="mx-2 h-4" />
                            <Badge variant="secondary" className="rounded-sm px-1 font-normal lg:hidden">
                              {values.offers.length}
                            </Badge>
                            <div className="hidden space-x-1 lg:flex">
                              {values.offers.length > 2 ? (
                                <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                                  {values.offers.length} selecionado(s)
                                </Badge>
                              ) : (
                                values.offers.map((offer) => (
                                  <Badge variant="secondary" key={offer.id} className="rounded-sm px-1 font-normal">
                                    {offer.name}
                                  </Badge>
                                ))
                              )}
                            </div>
                          </>
                        )}
                      </div>
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </PopoverTrigger>

                  <PopoverContent align="start" className="z-[72] w-[300px] p-0">
                    <Command>
                      <CommandInput placeholder="Buscar ofertas" className="h-9" />
                      <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>
                      <CommandList>
                        <CommandGroup>
                          {offers
                            .filter((it) => it.id !== 'all')
                            .map((offer) => (
                              <CommandItem
                                key={offer.id}
                                onSelect={() => {
                                  setFieldValue(
                                    'offers',
                                    values.offers.some((o) => o.id === offer.id)
                                      ? values.offers.filter((o) => o.id !== offer.id)
                                      : [...values.offers, offer],
                                  )
                                }}
                              >
                                <Check
                                  className={cn(
                                    'mr-2 h-4 w-4',
                                    values.offers.some((o) => o.id === offer.id) ? 'opacity-100' : 'opacity-0',
                                  )}
                                />
                                <span>{offer.name}</span>
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}

              <div className="flex flex-start items-center space-x-2">
                <Checkbox
                  id="applyToSpecificIntervals"
                  checked={values.applyToSpecificIntervals}
                  onCheckedChange={(checked) => setFieldValue('applyToSpecificIntervals', checked)}
                />
                <Label htmlFor="applyToSpecificIntervals">{t('createCoupon.applyToSpecificIntervals')}</Label>
              </div>

              {values.applyToSpecificIntervals && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between">
                      <div className="flex items-center">
                        <span className="text-sm">{t('createCoupon.intervals')}</span>
                        {values.intervals.length > 0 && (
                          <>
                            <Separator orientation="vertical" className="mx-2 h-4" />
                            <Badge variant="secondary" className="rounded-sm px-1 font-normal lg:hidden">
                              {values.intervals.length}
                            </Badge>
                            <div className="hidden space-x-1 lg:flex">
                              {values.intervals.length > 2 ? (
                                <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                                  {values.intervals.length} selecionado(s)
                                </Badge>
                              ) : (
                                values.intervals.map((interval) => (
                                  <Badge variant="secondary" key={interval} className="rounded-sm px-1 font-normal">
                                    {t(`planType.${interval}`)}
                                  </Badge>
                                ))
                              )}
                            </div>
                          </>
                        )}
                      </div>
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="z-[72] w-[300px] p-0">
                    <Command>
                      <CommandInput placeholder="Planos de pagamento" className="h-9" />
                      <CommandEmpty>{t('createCoupon.noIntervalsFound')}</CommandEmpty>
                      <CommandList>
                        <CommandGroup>
                          {Object.values({ ...RecurringInterval, ...LifetimeInterval }).map((interval) => (
                            <CommandItem
                              key={interval}
                              onSelect={() => {
                                setFieldValue(
                                  'intervals',
                                  values.intervals.includes(interval)
                                    ? values.intervals.filter((i) => i !== interval)
                                    : [...values.intervals, interval],
                                )
                              }}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  values.intervals.includes(interval) ? 'opacity-100' : 'opacity-0',
                                )}
                              />
                              <span>{t(`planType.${interval}`)}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}

              <div className="flex flex-start items-center space-x-2">
                <Checkbox
                  id="isLimitUsageEnabled"
                  checked={isLimitUsageEnabled}
                  onCheckedChange={(checked) => setIsLimitUsageEnabled(!!checked)}
                />
                <Label htmlFor="isLimitUsageEnabled">{t('createCoupon.isLimitUsageEnabled')}</Label>
              </div>

              {isLimitUsageEnabled && (
                <div>
                  <Label htmlFor="maxAllowedTotalUsages">{t('createCoupon.maxAllowedTotalUsages')}</Label>
                  <Input
                    id="maxAllowedTotalUsages"
                    name="maxAllowedTotalUsages"
                    type="number"
                    value={values.maxAllowedTotalUsages}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="mt-1"
                    aria-invalid={!!(errors.maxAllowedTotalUsages && touched.maxAllowedTotalUsages && !dirty)}
                    aria-describedby="maxAllowedTotalUsages-error"
                  />
                  {errors.maxAllowedTotalUsages && touched.maxAllowedTotalUsages && !dirty && (
                    <p id="maxAllowedTotalUsages-error" className="mt-1 text-sm text-red-500">
                      {errors.maxAllowedTotalUsages}
                    </p>
                  )}
                </div>
              )}

              <div className="flex flex-start items-center space-x-2">
                <Checkbox
                  id="isValidUntilEnabled"
                  checked={isValidUntilEnabled}
                  onCheckedChange={(checked) => setIsValidUntilEnabled(!!checked)}
                />
                <Label htmlFor="isValidUntilEnabled">{t('createCoupon.isValidUntilEnabled')}</Label>
              </div>

              {isValidUntilEnabled && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline">
                      <DateFormatter date={values.validUntil} fallbackText="Selecionar data" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="z-[72] w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={values.validUntil ? new Date(values.validUntil) : undefined}
                      onSelect={(date) => setFieldValue('validUntil', date ? date.toISOString() : undefined)}
                      locale={ptBR}
                      disabled={(date) => {
                        const today = moment().startOf('day')
                        return moment(date).isBefore(today)
                      }}
                    />
                  </PopoverContent>
                </Popover>
              )}

              <div className="flex flex-start items-center space-x-2">
                <Checkbox
                  id="isRecurrent"
                  checked={values.isRecurrent}
                  onCheckedChange={(checked) => setFieldValue('isRecurrent', checked)}
                />
                <Label htmlFor="isRecurrent">{t('createCoupon.isRecurrent')}</Label>
              </div>

              <div className="flex flex-start items-center space-x-2">
                <Checkbox
                  id="useOutsideCheckout"
                  checked={values.useOutsideCheckout}
                  onCheckedChange={(checked) => setFieldValue('useOutsideCheckout', checked)}
                />
                <Label htmlFor="useOutsideCheckout">{t('createCoupon.useOutsideCheckout')}</Label>
              </div>
            </div>
          </div>

          <SheetFooter className="sm:justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCloseModal} disabled={isLoading || loading}>
              {t('createCoupon.cancel')}
            </Button>
            <Button onClick={submitForm} disabled={!isValid} loading={isLoading || loading}>
              {t('createCoupon.confirm')}
            </Button>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  )
}
